/**
 * Web Lottie player wrapper.
 * Requires: npm install lottie-web
 * For URL-based animations (storyImage), loads JSON via fetch then plays with lottie-web.
 */
import React, {useEffect, useRef} from 'react';

interface LottieWebPlayerProps {
  source: object | string | null;
  style?: React.CSSProperties;
  autoPlay?: boolean;
  loop?: boolean;
}

const LottieWebPlayer: React.FC<LottieWebPlayerProps> = ({
  source,
  style,
  autoPlay = true,
  loop = true,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const animRef = useRef<any>(null);

  useEffect(() => {
    if (!containerRef.current || !source) return;

    let cancelled = false;

    const loadAnimation = async () => {
      try {
        const lottie = await import('lottie-web' as any);
        const lottieLib = (lottie as any).default ?? lottie;

        if (animRef.current) {
          animRef.current.destroy();
          animRef.current = null;
        }

        let animationData: object | null = null;

        if (typeof source === 'string') {
          const res = await fetch(source);
          animationData = await res.json();
        } else {
          animationData = source as object;
        }

        if (cancelled || !containerRef.current) return;

        animRef.current = lottieLib.loadAnimation({
          container: containerRef.current,
          renderer: 'svg',
          loop,
          autoplay: autoPlay,
          animationData,
        });
      } catch {
        // lottie-web not installed or fetch failed — container stays empty
      }
    };

    loadAnimation();

    return () => {
      cancelled = true;
      if (animRef.current) {
        animRef.current.destroy();
        animRef.current = null;
      }
    };
  }, [source, autoPlay, loop]);

  return (
    <div
      ref={containerRef}
      style={{display: 'flex', alignItems: 'center', justifyContent: 'center', ...style}}
    />
  );
};

export default LottieWebPlayer;
